import { useEffect, useRef, useState } from "react";
import { BUDGET_TIPS, BUDGET_TIPS_SOURCE } from "@/lib/budget_tips";

/**
 * Tip popover button (💡) for each ICAA chapter header.
 * Click toggles a panel anchored to the right of the header showing
 * professional advice extracted from the Bahar / IDA 2025 article.
 */
export default function BudgetTip({ chapter }) {
  const data = BUDGET_TIPS[chapter];
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!data) return null;

  return (
    <div ref={wrap} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        data-testid={`tip-btn-${chapter}`}
        title={`Consejos profesionales · Cap.${chapter}`}
        className={`flex h-7 w-7 items-center justify-center rounded-full text-[13px] transition ${
          open
            ? "bg-[#941620] text-stone-900 ring-2 ring-[#941620]/40"
            : "bg-white/10 text-[#941620] hover:bg-white/20"
        }`}
      >
        <span aria-hidden>💡</span>
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={`tip-panel-${chapter}`}
          className="absolute right-0 top-9 z-50 w-[340px] rounded-xl border border-stone-200 bg-white text-stone-900 shadow-2xl sm:w-[420px]"
        >
          <div className="rounded-t-xl bg-[#941620]/15 px-4 py-3">
            <p className="text-[9px] font-semibold uppercase tracking-[0.32em] text-[#a96f15]">
              Cap.{chapter} · Consejo profesional
            </p>
            <h3 className="mt-1 font-display text-lg leading-tight">{data.title}</h3>
            <p className="mt-0.5 text-[11px] italic text-stone-500">
              {data.industry_label}
            </p>
          </div>

          <div className="space-y-3 px-4 py-3 text-[13px] leading-relaxed text-stone-800">
            <p>{data.summary}</p>

            {data.ratios?.length > 0 && (
              <div>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-[#26775E]">
                  Ratios y criterios
                </p>
                <ul className="list-disc space-y-1 pl-4">
                  {data.ratios.map((r, i) => (
                    <li key={i} className="text-[12.5px]">{r}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-lg border-l-4 border-[#26775E] bg-[#26775E]/5 px-3 py-2 text-[12px] italic text-stone-700">
              {data.tip}
            </div>
          </div>

          <div className="rounded-b-xl border-t border-stone-100 bg-stone-50 px-4 py-2 text-[10px] text-stone-500">
            Fuente: <strong>{BUDGET_TIPS_SOURCE.author}</strong>, <em>{BUDGET_TIPS_SOURCE.title}</em>, {BUDGET_TIPS_SOURCE.publisher}, {BUDGET_TIPS_SOURCE.year}.{" "}
            <a href={BUDGET_TIPS_SOURCE.url} target="_blank" rel="noreferrer"
               className="text-[#26775E] underline hover:text-[#1c5c48]">
              Leer artículo ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
